import React, { useState, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Modal } from '../Modal';
import DownloadIcon from '!svg-react-loader!images/igniter/svgs/download.svg';

const STEP_MEDIA_TYPE = 'media_type';
const STEP_SUB_TYPE_TAG = 'sub_type_tag';
const STEP_FORMAT_SELECTION = 'format_selection';

function encodeDownloadParams(filetype, resolution, productTagId) {
  const parts = [filetype, resolution || ''];
  if (productTagId) parts.push(productTagId);
  return btoa(parts.join('/'));
}

function buildDownloadUrl(downloadHostname, collectionId, siteProductTypeId, encodedParams) {
  return Routes.igniter_collection_start_download_url(collectionId, siteProductTypeId, encodedParams, { host: downloadHostname });
}

function savePreferences(optionsHash, resolution, filetype) {
  const csrfToken = document.querySelector('meta[name="csrf-token"]');
  if (!csrfToken) return;

  fetch('/download/defaults', {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'X-CSRF-Token': csrfToken.content,
    },
    body: new URLSearchParams({
      resolutionFormatOptionsHash: optionsHash,
      resolution: resolution || '',
      fileformat: filetype,
    }),
  }).catch(err => console.error('Failed to save download preferences:', err));
}

function getStoredPreference(optionsHash) {
  if (typeof window !== 'undefined' && window.widget_defaults && window.widget_defaults[optionsHash]) {
    return window.widget_defaults[optionsHash];
  }
  return null;
}

function CollectionDownloadModal({
  section_name: sectionName,
  media_types: mediaTypes,
  sub_type_tags: subTypeTags = [],
  steps = ['media_type', 'format_selection'],
  download_hostname: downloadHostname,
  collection_id: collectionId,
  site_product_type_id: siteProductTypeId,
  format_data: formatData = {},
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(STEP_MEDIA_TYPE);
  const [selectedMediaType, setSelectedMediaType] = useState(null);
  const [selectedSubTypeTag, setSelectedSubTypeTag] = useState(null);
  const [selectedFormatIndex, setSelectedFormatIndex] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const hasSubTypeStep = useMemo(
    () => steps.includes(STEP_SUB_TYPE_TAG) && subTypeTags.length > 0,
    [steps, subTypeTags],
  );

  const nextStepAfterMediaType = hasSubTypeStep ? STEP_SUB_TYPE_TAG : STEP_FORMAT_SELECTION;

  const handleOpen = useCallback(() => {
    if (mediaTypes.length === 1) {
      setSelectedMediaType(mediaTypes[0].key);
      setCurrentStep(nextStepAfterMediaType);
    } else {
      setCurrentStep(STEP_MEDIA_TYPE);
      setSelectedMediaType(null);
    }
    setSelectedSubTypeTag(null);
    setSelectedFormatIndex(null);
    setDownloading(false);
    setIsOpen(true);
  }, [mediaTypes, nextStepAfterMediaType]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleMediaTypeSelect = useCallback((mediaTypeKey) => {
    setSelectedMediaType(mediaTypeKey);
    setCurrentStep(nextStepAfterMediaType);
  }, [nextStepAfterMediaType]);

  const handleSubTypeTagSelect = useCallback((tagId) => {
    setSelectedSubTypeTag(tagId);
    setCurrentStep(STEP_FORMAT_SELECTION);
  }, []);

  const handleBack = useCallback(() => {
    if (currentStep === STEP_SUB_TYPE_TAG) {
      setCurrentStep(STEP_MEDIA_TYPE);
      setSelectedMediaType(null);
    } else if (currentStep === STEP_FORMAT_SELECTION) {
      setSelectedFormatIndex(null);
      if (hasSubTypeStep) {
        setCurrentStep(STEP_SUB_TYPE_TAG);
        setSelectedSubTypeTag(null);
      } else {
        setCurrentStep(STEP_MEDIA_TYPE);
        setSelectedMediaType(null);
      }
    }
  }, [currentStep, hasSubTypeStep]);

  useEffect(() => {
    if (mediaTypes.length === 0) return;

    const handleBulkDownloadOpen = (e) => {
      const { collectionId: cid, siteProductTypeId: sptid } = e.detail;
      if (cid === collectionId && sptid === siteProductTypeId) {
        handleOpen();
      }
    };

    document.addEventListener('bulk-download:open', handleBulkDownloadOpen);
    return () => document.removeEventListener('bulk-download:open', handleBulkDownloadOpen);
  }, [collectionId, siteProductTypeId, mediaTypes, handleOpen]);

  if (mediaTypes.length === 0) {
    return null;
  }

  // Back button is shown when there's a previous step worth navigating to.
  // When mediaTypes.length === 1 and !hasSubTypeStep, format_selection is the
  // first visible step (media type was auto-selected), so back is hidden.
  const showBackButton = (currentStep === STEP_SUB_TYPE_TAG)
    || (currentStep === STEP_FORMAT_SELECTION && (hasSubTypeStep || mediaTypes.length > 1));

  return (
    <Modal isOpen={isOpen} onRequestClose={handleClose} className="collection-download-modal">
      <div className="collection-download-modal__header">
        <h3>
          <DownloadIcon className="collection-download-modal__download-icon" />
          {sectionName}
        </h3>
        {currentStep === STEP_MEDIA_TYPE && (
          <p className="collection-download-modal__subtitle">SELECT YOUR MEDIA TYPE</p>
        )}
        {currentStep === STEP_SUB_TYPE_TAG && (
          <p className="collection-download-modal__subtitle">SELECT YOUR SUB-TYPE</p>
        )}
        {currentStep === STEP_FORMAT_SELECTION && (
          <p className="collection-download-modal__subtitle">SELECT YOUR FILE TYPE</p>
        )}
      </div>

      {currentStep === STEP_MEDIA_TYPE && (
        <div className="collection-download-modal__options">
          {mediaTypes.map(type => (
            <button
              key={type.key}
              type="button"
              className="collection-download-modal__option"
              onClick={() => handleMediaTypeSelect(type.key)}
              data-media-type={type.key}
            >
              {type.label}
            </button>
          ))}
        </div>
      )}

      {currentStep === STEP_SUB_TYPE_TAG && (
        <div className="collection-download-modal__options">
          {subTypeTags.map(tag => (
            <button
              key={tag.id}
              type="button"
              className="collection-download-modal__option"
              onClick={() => handleSubTypeTagSelect(tag.id)}
              data-sub-type-tag={tag.id}
            >
              {tag.name}
            </button>
          ))}
          <div className="collection-download-modal__actions">
            <button
              type="button"
              className="collection-download-modal__back"
              onClick={handleBack}
            >
              BACK
            </button>
          </div>
        </div>
      )}

      {currentStep === STEP_FORMAT_SELECTION && (
        <FormatSelectionStep
          formatData={formatData}
          selectedMediaType={selectedMediaType}
          selectedSubTypeTag={selectedSubTypeTag}
          downloadHostname={downloadHostname}
          collectionId={collectionId}
          siteProductTypeId={siteProductTypeId}
          selectedFormatIndex={selectedFormatIndex}
          onSelectFormat={setSelectedFormatIndex}
          downloading={downloading}
          onDownloading={setDownloading}
          showBackButton={showBackButton}
          onBack={handleBack}
        />
      )}
    </Modal>
  );
}

function FormatSelectionStep({
  formatData,
  selectedMediaType,
  selectedSubTypeTag,
  downloadHostname,
  collectionId,
  siteProductTypeId,
  selectedFormatIndex,
  onSelectFormat,
  downloading,
  onDownloading,
  showBackButton,
  onBack,
}) {
  const tagKey = selectedSubTypeTag ? String(selectedSubTypeTag) : 'default';
  const mediaTypeVariants = formatData[selectedMediaType] || {};
  const mediaTypeData = mediaTypeVariants[tagKey] || mediaTypeVariants['default'] || {};
  const optionsHash = mediaTypeData.options_hash || '';
  const formatOptions = useMemo(() => mediaTypeData.format_options || [], [optionsHash]); // eslint-disable-line react-hooks/exhaustive-deps
  const isProjectFiles = selectedMediaType === 'project_files';

  // Pre-populate from saved preferences when format options change
  useEffect(() => {
    const stored = getStoredPreference(optionsHash);
    if (stored && stored.length === 2) {
      const [savedResolution, savedFiletype] = stored;
      const matchIdx = formatOptions.findIndex(opt =>
        opt.filetype === savedFiletype && (isProjectFiles || opt.resolution === savedResolution)
      );
      if (matchIdx >= 0) {
        onSelectFormat(matchIdx);
        return;
      }
    }
    if (formatOptions.length > 0) {
      onSelectFormat(0);
    }
  }, [optionsHash, isProjectFiles, onSelectFormat]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDownload = useCallback(() => {
    if (downloading || selectedFormatIndex === null || !formatOptions[selectedFormatIndex]) return;
    onDownloading(true);

    const option = formatOptions[selectedFormatIndex];
    const encodedParams = encodeDownloadParams(option.filetype, option.resolution, selectedSubTypeTag);
    const url = buildDownloadUrl(downloadHostname, collectionId, siteProductTypeId, encodedParams);

    savePreferences(optionsHash, option.resolution, option.filetype);

    // Update window.widget_defaults for in-page consistency
    if (typeof window !== 'undefined') {
      window.widget_defaults = window.widget_defaults || {};
      window.widget_defaults[optionsHash] = [option.resolution || '', option.filetype];
    }

    window.location.href = url;

    // Content-Disposition downloads don't navigate away, so reset the button
    // after a short delay to avoid permanently disabling it.
    setTimeout(() => onDownloading(false), 3000);
  }, [downloading, onDownloading, selectedFormatIndex, formatOptions, selectedSubTypeTag, collectionId, siteProductTypeId, optionsHash]);

  const handleListKeyDown = useCallback((e) => {
    if (selectedFormatIndex === null) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      onSelectFormat(Math.min(selectedFormatIndex + 1, formatOptions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      onSelectFormat(Math.max(selectedFormatIndex - 1, 0));
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleDownload();
    }
  }, [selectedFormatIndex, formatOptions.length, onSelectFormat, handleDownload]);

  if (formatOptions.length === 0) {
    return (
      <div className="collection-download-modal__format-selection">
        <p>No download formats available.</p>
      </div>
    );
  }

  return (
    <div className="collection-download-modal__format-selection">
      <ul
        className="collection-download-modal__format-list"
        role="listbox"
        tabIndex={0}
        aria-activedescendant={selectedFormatIndex !== null ? `format-option-${selectedFormatIndex}` : undefined}
        onKeyDown={handleListKeyDown}
      >
        {formatOptions.map((option, idx) => {
          const isSelected = selectedFormatIndex === idx;
          const fileCount = `(${option.count} ${option.count === 1 ? 'file' : 'files'})`;

          return (
            <li
              key={`${option.filetype}-${option.resolution || 'none'}`}
              id={`format-option-${idx}`}
              role="option"
              aria-selected={isSelected}
              className={`collection-download-modal__format-item${isSelected ? ' active' : ''}`}
              onClick={() => onSelectFormat(idx)}
              data-filetype={option.filetype}
              data-resolution={option.resolution}
            >
              {isSelected && <span className="collection-download-modal__check" aria-hidden="true">&#x2714;</span>}
              <div className="collection-download-modal__format-label">
                <span>
                  {isProjectFiles ? option.display_filetype : `${option.display_filetype} \u2014 ${option.resolution}`}
                </span>
                <span className="collection-download-modal__format-label__count">{fileCount}</span>
              </div>
            </li>
          );
        })}
      </ul>

      <div className="collection-download-modal__actions">
        <button
          type="button"
          className="collection-download-modal__download button"
          onClick={handleDownload}
          disabled={selectedFormatIndex === null || downloading}
          data-testid="download-button"
        >
          DOWNLOAD
        </button>

        {showBackButton && (
          <button
            type="button"
            className="collection-download-modal__back"
            onClick={onBack}
          >
            BACK
          </button>
        )}
      </div>
    </div>
  );
}

FormatSelectionStep.propTypes = {
  formatData: PropTypes.object.isRequired,
  selectedMediaType: PropTypes.string,
  selectedSubTypeTag: PropTypes.number,
  downloadHostname: PropTypes.string.isRequired,
  collectionId: PropTypes.number.isRequired,
  siteProductTypeId: PropTypes.number.isRequired,
  selectedFormatIndex: PropTypes.number,
  onSelectFormat: PropTypes.func.isRequired,
  downloading: PropTypes.bool.isRequired,
  onDownloading: PropTypes.func.isRequired,
  showBackButton: PropTypes.bool,
  onBack: PropTypes.func,
};

CollectionDownloadModal.propTypes = {
  section_name: PropTypes.string.isRequired,
  media_types: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  sub_type_tags: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ),
  format_data: PropTypes.object,
  steps: PropTypes.arrayOf(PropTypes.string),
  download_hostname: PropTypes.string.isRequired,
  collection_id: PropTypes.number.isRequired,
  site_product_type_id: PropTypes.number.isRequired,
};

export default CollectionDownloadModal;
